/**
 * End a task RMF is STILL EXECUTING behind a stale console record.
 *
 * THE CASE, AND WHY THERE WAS NO BUTTON FOR IT (sg-nomad-01, 2026-09-15).
 * Worker 0.4.28 projected RMF's `error` -- which means "needs attention, still
 * running", and which the fleet adapter re-enters every 5 s while it retries a
 * plan -- as the terminal `failed`. For 47 minutes:
 *
 *   * the operator console hid Cancel and Kill (offered on non-terminal
 *     states only);
 *   * Retire refused the task as already ended, at 13:10 and again at 13:14,
 *     WITH NO ALTERNATIVE OFFERED (the day record, OF:281);
 *   * the only thing that worked at 13:25:51 was a hand-rolled POST.
 *
 * This is that alternative, and it sits beside Retire because that is where
 * the engineer already is when they hit the refusal.
 *
 * IT IS NOT A RETIRE. Retire WRITES a synthetic terminal record onto the task
 * key and is for a task nothing owns; this publishes the same
 * `task.cancel`/`task.kill` a console button publishes and lets RMF
 * terminalise the task itself. If they were one verb, an engineer clearing a
 * stuck record would sometimes silently be commanding a robot.
 *
 * THE BUTTONS ARE ENABLED ONLY ON POSITIVE EVIDENCE: `held === 'held'` (a
 * FRESH robot state naming this task) AND no lift. Everything else is
 * disabled with the reason on screen, because the endpoint refuses the same
 * cases and a button that produces a 409 teaches an engineer to ignore the
 * screen.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StatusChip } from '@shared/design-system/components';
import { useAuth } from '@shared/auth/useAuth';
import {
  endHeldTask,
  getOrphanTaskEvidence,
  type EndHeldTask as EndHeldTaskResult,
} from '../../api/supportClient';
import {
  RETIRE_REASON_MAX,
  TASK_ID_RE,
  canRetireTasks,
  endHeldBlockedReason,
  errorText,
  isRetireReasonValid,
  retireReasonLength,
} from './retire-task-rules';

/** The 409 codes this endpoint can answer with, rendered as themselves. */
const REFUSAL_TEXT: Record<string, string> = {
  task_ownership_unknown:
    'Refused: the platform cannot tell whether a robot is still executing this task. Nothing was sent.',
  task_not_held: 'Refused: no robot is executing this task. Nothing was sent.',
  lift_state_unreadable:
    'Refused: the lift state could not be read, so whether the robot is in a car is unknown. Nothing was sent.',
  robot_in_lift:
    'Refused: the robot is in or boarding a lift car. Use the lift procedure — hold the door, immobilise the robot, push it at least 1.2 m onto the landing, then press Recover.',
};

export function EndHeldTask({ site, siteLabel }: { site: string; siteLabel: string }) {
  const { roles } = useAuth();
  // Nothing at all for any other role, exactly like RetireOrphanedTask:
  // platform_admin only (action plan §3e). A disabled control would tell a
  // support engineer a lever exists that they will never be able to pull.
  if (!canRetireTasks(roles)) return null;
  return (
    <section
      data-testid="end-held-task"
      style={{ borderTop: '1px solid var(--nx-border)', marginTop: 12, paddingTop: 12 }}
    >
      <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>End task held by RMF</h3>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--nx-muted)' }}>
        For a task whose record says it ended while a robot is still executing it. This sends the
        same Cancel or Kill the operator console sends — it never writes the task record, so it is
        not a retire.
      </p>
      <EndHeldTaskControl site={site} siteLabel={siteLabel} />
    </section>
  );
}

function EndHeldTaskControl({ site, siteLabel }: { site: string; siteLabel: string }) {
  const qc = useQueryClient();
  const [taskInput, setTaskInput] = useState('');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [action, setAction] = useState<'cancel' | 'kill' | null>(null);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<EndHeldTaskResult | null>(null);

  // A different target site is a different task. Everything typed or
  // confirmed against the old one is dropped rather than silently re-aimed
  // -- the same rule RetireOrphanedTask follows, for the same reason.
  useEffect(() => {
    setTaskId(null);
    setAction(null);
    setReason('');
    setResult(null);
  }, [site]);

  // THE SAME QUERY KEY the retire card uses, so one read serves both cards
  // and the two cannot show different verdicts about the same task at the
  // same moment.
  const evidenceKey = ['support', 'orphan-evidence', site, taskId] as const;
  const evidence = useQuery({
    queryKey: evidenceKey,
    queryFn: () => getOrphanTaskEvidence(site, taskId as string),
    enabled: Boolean(site && taskId),
    retry: false,
  });

  const end = useMutation({
    mutationFn: () => endHeldTask(site, taskId as string, action as 'cancel' | 'kill', reason),
    onSuccess: (resp) => {
      setResult(resp);
      setAction(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: evidenceKey });
    },
  });

  const trimmedInput = taskInput.trim();
  const inputValid = TASK_ID_RE.test(trimmedInput);
  const ev = evidence.data;
  const blocked = endHeldBlockedReason(ev);

  if (!site) {
    return (
      <p style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
        Choose a target site above to end a held task there.
      </p>
    );
  }

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!inputValid) return;
          setTaskId(trimmedInput);
          setAction(null);
          setReason('');
          setResult(null);
          end.reset();
        }}
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <label htmlFor="end-held-task-id" style={{ fontSize: 13 }}>
          Task id at {siteLabel || site}
        </label>
        <input
          id="end-held-task-id"
          value={taskInput}
          onChange={(e) => setTaskInput(e.target.value)}
          placeholder="compose.dispatch-1c92c2e760"
          spellCheck={false}
        />
        <button type="submit" disabled={!inputValid}>
          Check ownership
        </button>
      </form>

      {taskId && evidence.isPending && <p style={{ fontSize: 13 }}>Reading ownership…</p>}
      {taskId && evidence.isError && (
        <p data-testid="end-held-evidence-error" style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
          Could not read the ownership ({errorText(evidence.error)}). The controls stay disabled:
          this verb moves a robot and must not act on a guess.
        </p>
      )}

      {ev && (
        <div data-testid="end-held-evidence" style={{ marginTop: 8, fontSize: 13 }}>
          <StatusChip
            status={ev.held === 'held' ? 'warning' : 'idle'}
            label={`RMF ownership: ${ev.held ?? 'unknown'}`}
          />{' '}
          {ev.assigned_robot ?? 'no robot'}
          {typeof ev.robot_state_age_ms === 'number'
            ? ` · robot state ${Math.round(ev.robot_state_age_ms / 1000)} s old`
            : ''}
          {ev.rmf_status ? ` · RMF reports ${ev.rmf_status}` : ''}
        </div>
      )}

      {blocked && taskId && !evidence.isPending && (
        <p data-testid="end-held-blocked" style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
          {blocked}
        </p>
      )}

      {taskId && !blocked && !action && !result && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="button" data-testid="end-held-cancel-btn" onClick={() => setAction('cancel')}>
            Cancel task…
          </button>
          <button type="button" data-testid="end-held-kill-btn" onClick={() => setAction('kill')}>
            Kill task…
          </button>
        </div>
      )}

      {action && (
        <div
          role="group"
          aria-label="Confirm end held task"
          data-testid="end-held-confirm"
          style={{
            border: '1px solid var(--nx-error)',
            borderRadius: 'var(--nx-r)',
            padding: 12,
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <strong>
            {action === 'cancel' ? 'Cancel' : 'Kill'} <code>{taskId}</code> at {siteLabel || site}?
          </strong>
          <p style={{ margin: 0, fontSize: 13 }}>
            {ev?.assigned_robot ?? 'The robot'} is executing this task now. This sends the same
            command the operator console sends and does not touch the task record.
            {action === 'kill'
              ? ' Kill stops immediately and may leave the robot encumbered.'
              : ' Cancel lets the robot reach an unencumbered state first.'}
          </p>
          <label htmlFor="end-held-reason" style={{ fontSize: 13 }}>
            Reason (required, recorded verbatim in the audit trail)
          </label>
          <textarea
            id="end-held-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="record read failed while the adapter was still driving it; operator asked us to end it"
          />
          <span
            style={{
              fontSize: 12,
              color:
                retireReasonLength(reason) > RETIRE_REASON_MAX
                  ? 'var(--nx-error)'
                  : 'var(--nx-muted)',
            }}
          >
            {retireReasonLength(reason)}/{RETIRE_REASON_MAX}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="end-held-confirm-btn"
              disabled={!isRetireReasonValid(reason) || end.isPending || Boolean(blocked)}
              onClick={() => end.mutate()}
            >
              {end.isPending ? 'Sending…' : action === 'cancel' ? 'Cancel task' : 'Kill task'}
            </button>
            <button type="button" onClick={() => setAction(null)}>
              Back
            </button>
          </div>
        </div>
      )}

      {end.isError && (
        <p data-testid="end-held-error" role="alert" style={{ color: 'var(--nx-error)', fontSize: 13 }}>
          {refusalText(end.error)}
        </p>
      )}

      {result && (
        <div
          role="status"
          data-testid="end-held-result"
          style={{
            border: '1px solid var(--nx-cyan)',
            borderRadius: 'var(--nx-r)',
            padding: 10,
            marginTop: 8,
            fontSize: 13,
          }}
        >
          <strong>
            {result.action === 'kill' ? 'Kill' : 'Cancel'} sent for <code>{result.task_id}</code>.
          </strong>{' '}
          {commandStateText(result)}
        </div>
      )}
    </div>
  );
}

function refusalText(error: unknown): string {
  const body = (error as { body?: unknown } | null)?.body;
  const detail = (body as { detail?: { code?: unknown; message?: unknown } } | undefined)?.detail;
  const code = typeof detail?.code === 'string' ? detail.code : undefined;
  if (code && REFUSAL_TEXT[code]) return REFUSAL_TEXT[code];
  if (typeof detail?.message === 'string') return detail.message;
  return errorText(error);
}

function commandStateText(result: EndHeldTaskResult): string {
  const state = (result.command as { state?: unknown }).state;
  if (state === 'observed_applied') {
    return 'RMF confirmed it. The task should leave the running list within a second or two.';
  }
  if (state === 'outcome_unknown') {
    // THE SENTENCE THAT MATTERS. Ten cancels read as successes during the
    // 45 h `patrol.dispatch-48` wedge because nothing said this; and the
    // wrong reflex -- restarting the adapter -- destroys the in-RAM task
    // the command was aimed at.
    return (
      'The command reached NO fleet adapter — nothing answered. Do not restart the adapter: ' +
      'that destroys the in-RAM task this was aimed at. Check the adapter is up and retry.'
    );
  }
  return `The platform recorded it as ${String(state ?? 'unknown')}.`;
}
